/**
 * SearchableSelect — กล่องเลือกแบบ Select2 ที่เขียนเอง ไม่พึ่งไลบรารี (D-136, WO-P2-M5-019)
 *
 * **component กลาง ไม่รู้จักสินค้า/แผนหรือโมดูลใดเลย** (เหมือน `DateRangePicker`, D-120) —
 * รับ `options` เป็น `{ value, label, searchText? }` ล้วน ๆ ผู้เรียกเป็นคนแปลข้อมูลโดเมน
 * ของตัวเองมาเป็นรูปนี้
 *
 * ปิด → แสดง label ของค่าที่เลือก (หรือค่าดิบถ้าไม่ตรงกับ options ใดเลย — กรณีรหัสจาก
 * แผนเดิมที่สินค้าถูกลบไปแล้ว ต้องไม่หายเงียบ) เปิด → panel มีช่องค้นหาโฟกัสทันทีและ
 * `role=listbox` กรองด้วยรหัส/ชื่อ/Model (ไม่สนตัวพิมพ์ หลายคำ = ต้องตรงทุกคำ)
 *
 * ⚠️ panel ใช้ `position: fixed` คำนวณจากตำแหน่งกล่องเสมอ ไม่ใช่ `position: absolute`
 * แบบ `DateRangePicker` — ตารางบรรทัดที่ใช้กล่องนี้อยู่ใน `.cap-table-wrap` ซึ่งตั้ง
 * `overflow-x: auto` ทำให้ `overflow-y` กลายเป็น `auto` ไปด้วยโดยปริยาย (สเปก CSS)
 * panel แบบ absolute จะถูกตัดครึ่งเมื่อแถวอยู่ใกล้ก้นตาราง
 *
 * ปุ่มล้าง (✕) เป็น `<button>` จริงซ้อนอยู่ใน trigger ที่เป็น `<div role="combobox">`
 * (ไม่ใช่ `<button>`) เพื่อเลี่ยง nested-button ที่ผิด HTML — `event.stopPropagation()`
 * ที่ปุ่มล้างกันไม่ให้ click ไปเปิด/ปิด panel ของ trigger ไปด้วย
 */

import { useEffect, useId, useRef, useState } from 'react';

import './searchable-select.css';

export interface SearchableSelectOption {
  readonly value: string;
  readonly label: string;
  /** ใช้ค้นหาแทน `label` เมื่อมี (เช่น รวมรหัส+ชื่อ+Model) */
  readonly searchText?: string;
}

export interface SearchableSelectProps {
  readonly options: readonly SearchableSelectOption[];
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly clearable?: boolean;
  /** เปิดตัวเลือก 'ใช้รหัส "…"' ท้ายรายการเมื่อคำค้นไม่ตรงกับ `value` ของตัวเลือกใดเป๊ะ ๆ */
  readonly allowCustom?: boolean;
  readonly emptyText?: string;
  readonly ariaLabel: string;
  /** คำนำหน้าของ `data-testid` ทุกตัว และเป็น `data-testid` ของกล่องเองโดยตรง */
  readonly testId?: string;
}

const DISPLAY_LIMIT = 200;
const DEFAULT_EMPTY_TEXT = 'ไม่พบรายการ';

type DisplayItem =
  | { readonly kind: 'option'; readonly option: SearchableSelectOption }
  | { readonly kind: 'custom'; readonly value: string; readonly label: string };

function splitWords(query: string): readonly string[] {
  return query.trim().toLocaleLowerCase().split(/\s+/).filter((word) => word !== '');
}

function matchesAllWords(haystack: string, words: readonly string[]): boolean {
  const normalized = haystack.toLocaleLowerCase();
  return words.every((word) => normalized.includes(word));
}

interface PanelPosition {
  readonly top: number;
  readonly left: number;
  readonly width: number;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  clearable = false,
  allowCustom = false,
  emptyText = DEFAULT_EMPTY_TEXT,
  ariaLabel,
  testId,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const optionRefs = useRef<Map<number, HTMLLIElement>>(new Map());

  const baseId = useId();
  const listboxId = `${baseId}-listbox`;

  const testIdFor = (suffix: string): string | undefined =>
    testId === undefined ? undefined : `${testId}-${suffix}`;

  const selectedOption = options.find((option) => option.value === value) ?? null;
  const triggerText = selectedOption?.label ?? (value.trim() !== '' ? value : '');

  const words = splitWords(query);
  const filtered =
    words.length === 0
      ? options
      : options.filter((option) => matchesAllWords(option.searchText ?? option.label, words));
  const totalMatches = filtered.length;
  const limited = filtered.slice(0, DISPLAY_LIMIT);

  const trimmedQuery = query.trim();
  const customValue = trimmedQuery.toUpperCase();
  const hasExactMatch = options.some((option) => option.value.toUpperCase() === customValue);
  const showCustom = allowCustom && customValue !== '' && !hasExactMatch;

  const items: readonly DisplayItem[] = [
    ...limited.map((option) => ({ kind: 'option' as const, option })),
    ...(showCustom ? [{ kind: 'custom' as const, value: customValue, label: `ใช้รหัส "${customValue}"` }] : []),
  ];

  useEffect(() => {
    setActiveIndex(-1);
  }, [query, open]);

  const reposition = () => {
    const trigger = triggerRef.current;
    if (trigger === null) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    setPanelPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    reposition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // panel เรนเดอร์หลังรู้ตำแหน่งแล้วเท่านั้น — โฟกัสช่องค้นหาตอนที่มันถูกสร้างจริง
  const panelReady = open && panelPosition !== null;
  useEffect(() => {
    if (panelReady) {
      searchRef.current?.focus();
    }
  }, [panelReady]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.stopPropagation();
      setOpen(false);
      setQuery('');
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', reposition);
    // capture:true — จับ scroll ของ container ข้างในด้วย (scroll ไม่ bubble ไปที่ window)
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (activeIndex < 0) {
      return;
    }
    optionRefs.current.get(activeIndex)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const openPanel = () => {
    if (disabled) {
      return;
    }
    setQuery('');
    setActiveIndex(-1);
    setOpen(true);
  };

  const closeWithoutChange = () => {
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  };

  const selectItem = (item: DisplayItem) => {
    const nextValue = item.kind === 'option' ? item.option.value : item.value;
    setOpen(false);
    setQuery('');
    onChange(nextValue);
    triggerRef.current?.focus();
  };

  const activeItemId = activeIndex >= 0 && activeIndex < items.length ? `${baseId}-option-${activeIndex}` : undefined;

  return (
    <div className="cap-searchable-select" ref={rootRef}>
      <div
        ref={triggerRef}
        className={`cap-input cap-searchable-select__trigger${triggerText === '' ? ' cap-searchable-select__trigger--empty' : ''}${disabled ? ' cap-searchable-select__trigger--disabled' : ''}`}
        data-testid={testId}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (disabled) {
            return;
          }
          if (open) {
            closeWithoutChange();
            return;
          }
          openPanel();
        }}
        onKeyDown={(event) => {
          if (disabled || open) {
            return;
          }
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPanel();
          }
        }}
      >
        <span className="cap-searchable-select__value">{triggerText === '' ? (placeholder ?? '') : triggerText}</span>
        <span className="cap-searchable-select__controls">
          {clearable && value !== '' ? (
            <button
              type="button"
              className="cap-searchable-select__clear"
              data-testid={testIdFor('clear')}
              aria-label={`ล้าง ${ariaLabel}`}
              onClick={(event) => {
                event.stopPropagation();
                onChange('');
              }}
            >
              ✕
            </button>
          ) : null}
          <span className="cap-searchable-select__arrow" aria-hidden="true">
            ▾
          </span>
        </span>
      </div>

      {open && panelPosition !== null ? (
        <div
          className="cap-searchable-select__panel"
          style={{ top: panelPosition.top, left: panelPosition.left, width: panelPosition.width }}
          data-testid={testIdFor('panel')}
        >
          <input
            ref={searchRef}
            type="text"
            className="cap-input cap-searchable-select__search"
            aria-label={`ค้นหา ${ariaLabel}`}
            aria-controls={listboxId}
            aria-activedescendant={activeItemId}
            data-testid={testIdFor('search')}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIndex((index) => Math.min(index + 1, items.length - 1));
                return;
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex((index) => Math.max(index - 1, 0));
                return;
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                const item = items[activeIndex];
                if (item !== undefined) {
                  selectItem(item);
                }
                return;
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                closeWithoutChange();
                return;
              }
              if (event.key === 'Tab') {
                setOpen(false);
                setQuery('');
              }
            }}
          />

          <ul id={listboxId} role="listbox" className="cap-searchable-select__options" data-testid={testIdFor('listbox')}>
            {items.map((item, index) => {
              const isSelected = item.kind === 'option' && item.option.value === value;
              return (
                <li
                  key={item.kind === 'option' ? item.option.value : `custom-${item.value}`}
                  id={`${baseId}-option-${index}`}
                  ref={(node) => {
                    if (node === null) {
                      optionRefs.current.delete(index);
                    } else {
                      optionRefs.current.set(index, node);
                    }
                  }}
                  role="option"
                  aria-selected={isSelected}
                  className={`cap-searchable-select__option${index === activeIndex ? ' cap-searchable-select__option--active' : ''}${item.kind === 'custom' ? ' cap-searchable-select__option--custom' : ''}`}
                  data-testid={item.kind === 'option' ? testIdFor(`option-${item.option.value}`) : testIdFor('option-custom')}
                  onMouseEnter={() => {
                    setActiveIndex(index);
                  }}
                  onMouseDown={(event) => {
                    // กัน blur ของ input ค้นหาไม่ให้ยิงก่อน click ของตัวเลือก
                    event.preventDefault();
                  }}
                  onClick={() => {
                    selectItem(item);
                  }}
                >
                  <span aria-hidden="true" className="cap-searchable-select__check">
                    {isSelected ? '✓' : ''}
                  </span>
                  {item.kind === 'option' ? item.option.label : item.label}
                </li>
              );
            })}
          </ul>

          {totalMatches > DISPLAY_LIMIT ? (
            <p className="cap-searchable-select__status" data-testid={testIdFor('status')}>
              แสดง {DISPLAY_LIMIT} จาก {totalMatches} รายการ — พิมพ์เพื่อกรอง
            </p>
          ) : null}

          {items.length === 0 ? (
            <p className="cap-searchable-select__empty" data-testid={testIdFor('empty')}>
              {emptyText}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
